/**
 * Queries and mutations for the pipeline.
 *
 * ===========================================================================
 * THE OPTIMISTIC CACHE EDIT MOVES THE ROW BETWEEN COLUMNS — IT DOES NOT JUST
 * REWRITE A FIELD
 * ===========================================================================
 * The Board renders FIVE independent list queries, one per stage
 * (`?stage=new`, `?stage=need_quote`, …). A naive optimistic update that only changed
 * `row.stage` in place would leave the dragged card sitting in the column it came from,
 * now labelled with the wrong stage, until a refetch landed — the one visual the drag
 * gesture exists to produce would be missing.
 *
 * So {@link useStageMoveController} walks every cached inquiry list for the country and,
 * per list, does the one correct thing for THAT list's `stage` filter: rewrite the row,
 * remove it, or insert it. The insert is placed by `created_at` DESC then `id` DESC —
 * which is not a guess, it is the API's own `ORDER BY`
 * (`inquiries.service.ts`: `.orderBy('i.created_at','DESC').addOrderBy('i.id','DESC')`).
 * Reproducing the documented order means the optimistic position is the position the
 * refetch will confirm, instead of a card that jumps after the response arrives.
 *
 * ROLLBACK is a snapshot restore, not a reverse edit. Every touched cache entry is
 * captured before the write and restored verbatim on failure. Attempting to "un-apply"
 * the edit arithmetically would drift the moment two moves overlapped.
 *
 * UNDO is the opposite of that: a second, real `PATCH` (Part A). It runs through the same
 * mutation, so it gets the same optimistic treatment, the same rollback, and — the point —
 * its own `inquiry.stage_changed` event. The audit trail shows both moves.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { fetchInquiries, fetchStageCounts, inquiryKeys } from '../../lib/inquiries-api';
import type { InquiryDto, InquiryRecordDto, Paged, StageCountsDto } from '../../lib/inquiries-api';
import type { InquiryStage } from '../../domain/stages';
import type { EventRow } from '../../domain/events';
import { ApiRequestError } from '../../lib/api';
import {
  bulkUpdateInquiries,
  changeInquiryStage,
  createFollowup,
  createInquiry,
  createParty,
  fetchInquiry,
  fetchInquiryEvents,
  fetchInquiryPlatformOptions,
  fetchParty,
  partyKeys,
  searchParties,
  inquiryEventKeys,
  updateInquiry,
} from './api';
import type {
  BulkAction,
  BulkResultDto,
  CreateFollowupBody,
  CreateInquiryBody,
  CreatePartyBody,
  InquiryPlatformEntry,
  InquiryPlatformOptionDto,
  PartyDto,
} from './api';
import { listParamsFor } from './query-state';
import type { InquiryQueryState } from './query-state';
import {
  applyMoveToCounts,
  applyMoveToRows,
  moveToastText,
  planStageMove,
  planUndo,
  refusalText,
  undoToastText,
} from './stage-move';
import type { MovePlan, MoveSource, StageMove } from './stage-move';
import { useToast } from './toast';

/* ─────────────────────────────────────────────────────────────────── reads ── */

export function useStageCounts(
  countryCode: string,
  enabled = true,
): UseQueryResult<StageCountsDto> {
  return useQuery({
    queryKey: inquiryKeys.stageCounts(countryCode),
    queryFn: ({ signal }) => fetchStageCounts(countryCode, signal),
    enabled,
  });
}

/**
 * The table's page.
 *
 * `placeholderData: keepPreviousData` keeps the previous page on screen while the next one
 * loads. Without it, paging blanks the table to a skeleton for one round trip and the row
 * the operator was reading jumps — the classic "did I lose my place?" flicker.
 */
export function useInquiryList(
  countryCode: string,
  state: InquiryQueryState,
  enabled = true,
): UseQueryResult<Paged<InquiryDto>> {
  const params = useMemo(() => listParamsFor(state), [state]);
  return useQuery({
    queryKey: inquiryKeys.list(countryCode, params),
    queryFn: ({ signal }) => fetchInquiries(countryCode, params, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** How many cards one board column loads before it says "N more". */
export const BOARD_COLUMN_PAGE = 25;

/**
 * One board column.
 *
 * `q` and `assigned` are inherited from the shared query state — the board is a different
 * VIEW of the same question, not a different question — while `stage` is fixed to this
 * column. `page` and `perPage` are NOT inherited: the table's page 3 has no meaning on a
 * board, and silently applying it would show an empty column to anybody who paged before
 * toggling.
 */
export function useBoardColumn(
  countryCode: string,
  stage: InquiryStage,
  state: InquiryQueryState,
  enabled = true,
): UseQueryResult<Paged<InquiryDto>> {
  const params = useMemo(
    () => ({
      stage,
      ...(state.assigned === null ? {} : { assigned: state.assigned }),
      ...(state.q.trim() === '' ? {} : { q: state.q.trim() }),
      page: 1,
      perPage: BOARD_COLUMN_PAGE,
    }),
    [stage, state.assigned, state.q],
  );
  return useQuery({
    queryKey: inquiryKeys.list(countryCode, params),
    queryFn: ({ signal }) => fetchInquiries(countryCode, params, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useInquiryRecord(id: string | null): UseQueryResult<InquiryRecordDto> {
  return useQuery({
    queryKey: inquiryKeys.record(id ?? ''),
    queryFn: ({ signal }) => fetchInquiry(id ?? '', signal),
    enabled: id !== null,
  });
}

/**
 * The party behind an inquiry — Contact / Phone / Email in `Record/PropertiesPanel`.
 *
 * Gated on `parties.view`, which is NOT implied by `inquiries.view` (spec Appendix A). A
 * caller without it must pass `enabled: false` and fall back to `party_name`, which the
 * inquiry row already carries; firing the request anyway would put a 403 where a phone
 * number belongs.
 */
export function useParty(id: string | null, enabled: boolean): UseQueryResult<PartyDto> {
  return useQuery({
    queryKey: partyKeys.record(id ?? ''),
    queryFn: ({ signal }) => fetchParty(id ?? '', signal),
    enabled: enabled && id !== null,
  });
}

export function useInquiryEvents(
  countryCode: string,
  inquiryId: string | null,
  enabled = true,
): UseQueryResult<Paged<EventRow>> {
  return useQuery({
    queryKey: inquiryEventKeys.forInquiry(countryCode, inquiryId ?? ''),
    queryFn: ({ signal }) => fetchInquiryEvents(countryCode, inquiryId ?? '', signal),
    enabled: enabled && inquiryId !== null,
  });
}

/**
 * The create screen's REQUIREMENTS picker (migration 48, owner ruling 5) — the server's
 * answer to "what may an inquiry in this country carry today". Read once per country and
 * shared across mounts through the query cache; a create invalidates the country prefix
 * and so refreshes it (see `inquiryKeys.platformOptions`).
 */
export function useInquiryPlatformOptions(
  countryCode: string,
  enabled = true,
): UseQueryResult<InquiryPlatformOptionDto[]> {
  return useQuery({
    queryKey: inquiryKeys.platformOptions(countryCode),
    queryFn: ({ signal }) => fetchInquiryPlatformOptions(countryCode, signal),
    enabled,
  });
}

/** Party typeahead. Disabled under two characters — a one-letter `LIKE '%a%'` is a scan. */
export function usePartySearch(
  countryCode: string,
  term: string,
  enabled: boolean,
): UseQueryResult<Paged<PartyDto>> {
  const trimmed = term.trim();
  return useQuery({
    queryKey: partyKeys.search(countryCode, trimmed),
    // A PICKER: history stays off (AM-1's ruled default). This panel offers no
    // toggle yet — flagged in the A1 report rather than silently widened.
    queryFn: ({ signal }) => searchParties(countryCode, trimmed, false, signal),
    enabled: enabled && trimmed.length >= 2,
    placeholderData: keepPreviousData,
  });
}

/* ────────────────────────────────────────────────────── cache manipulation ── */

type ListEntry = [QueryKey, Paged<InquiryDto> | undefined];
type Snapshot = { key: QueryKey; data: unknown };

/** Does a cached list's `?stage=` filter admit this stage? (Absent = the "All" tab.) */
function listAdmits(key: QueryKey, stage: InquiryStage): boolean {
  const params = key[3];
  if (typeof params !== 'object' || params === null) return true;
  const filter = (params as { stage?: unknown }).stage;
  if (typeof filter !== 'string' || filter === '') return true;
  return filter.split(',').some((part) => part.trim() === stage);
}

function perPageOf(key: QueryKey, fallback: number): number {
  const params = key[3];
  if (typeof params !== 'object' || params === null) return fallback;
  const value = (params as { perPage?: unknown }).perPage;
  return typeof value === 'number' && value > 0 ? value : fallback;
}

/**
 * Insert a row where the API would have put it: `created_at` DESC, then `id` DESC.
 *
 * Ids are compared as STRINGS (length first, then lexically) because `BIGINT UNSIGNED`
 * runs past 2^53 — `Number(id)` would make two different ids compare equal.
 */
function insertByServerOrder(
  rows: readonly InquiryDto[],
  row: InquiryDto,
  perPage: number,
): InquiryDto[] {
  const time = (value: string | null): number => {
    if (value === null) return Number.NEGATIVE_INFINITY;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
  };
  const newer = (a: InquiryDto, b: InquiryDto): boolean => {
    const delta = time(a.created_at) - time(b.created_at);
    if (delta !== 0) return delta > 0;
    if (a.id.length !== b.id.length) return a.id.length > b.id.length;
    return a.id > b.id;
  };

  const next = [...rows];
  let index = next.findIndex((candidate) => newer(row, candidate));
  if (index === -1) index = next.length;
  next.splice(index, 0, row);
  // Keep the page the size the operator was told it is. The refetch settles the rest.
  return next.slice(0, perPage);
}

/* ───────────────────────────────────────────────────────────── stage moves ── */

export interface StageMoveController {
  /**
   * Plan and (usually) send a move. Handles no-op, refusal and the reason prompt.
   *
   * `reason` lets a caller that ALREADY collected one skip the prompt — the follow-up
   * panel asks for it inline, because stacking a dialog on top of an open panel to
   * re-ask a question the operator just answered is a worse experience than either.
   */
  requestMove: (source: MoveSource, target: string, reason?: string) => void;
  /** Non-null while a `lost` move is waiting for its required reason. */
  awaitingReason: StageMove | null;
  submitReason: (reason: string) => void;
  cancelReason: () => void;
  isMoving: boolean;
}

interface MoveVariables {
  move: StageMove;
  body: { stage: InquiryStage; reason?: string };
  /** False for the undo of a move, so an undo cannot itself offer an Undo. */
  undoable: boolean;
}

export function useStageMoveController(countryCode: string): StageMoveController {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [awaitingReason, setAwaitingReason] = useState<StageMove | null>(null);

  /**
   * The last move whose Undo is still live.
   *
   * A ref, not state: it is read inside a toast callback that must see the value as of
   * the click, and re-rendering the whole page because an undo became available would
   * interrupt a drag in progress.
   */
  const lastMove = useRef<StageMove | null>(null);

  const mutation = useMutation<InquiryRecordDto, unknown, MoveVariables, Snapshot[]>({
    mutationFn: ({ move, body }) => changeInquiryStage(move.id, body),

    onMutate: ({ move }) => {
      const at = new Date().toISOString();
      const snapshots: Snapshot[] = [];

      // The row as it currently exists anywhere in the cache — needed to INSERT it into
      // the destination column. Without a copy, the destination can only be invalidated,
      // and the card would vanish for a round trip instead of moving.
      let subject: InquiryDto | null = null;
      const entries: ListEntry[] = queryClient.getQueriesData<Paged<InquiryDto>>({
        queryKey: ['inquiries', countryCode, 'list'],
      });

      for (const [, data] of entries) {
        const found = data?.data.find((row) => row.id === move.id);
        if (found !== undefined) {
          subject = found;
          break;
        }
      }

      for (const [key, data] of entries) {
        if (data === undefined) continue;
        snapshots.push({ key, data });

        const has = data.data.some((row) => row.id === move.id);
        const admits = listAdmits(key, move.to);

        if (has && admits) {
          queryClient.setQueryData<Paged<InquiryDto>>(key, {
            ...data,
            data: [...applyMoveToRows(data.data, move, at)],
          });
        } else if (has && !admits) {
          queryClient.setQueryData<Paged<InquiryDto>>(key, {
            ...data,
            data: data.data.filter((row) => row.id !== move.id),
            total: Math.max(0, data.total - 1),
          });
        } else if (!has && admits && subject !== null) {
          const moved = applyMoveToRows([subject], move, at)[0];
          if (moved !== undefined) {
            queryClient.setQueryData<Paged<InquiryDto>>(key, {
              ...data,
              data: insertByServerOrder(data.data, moved, perPageOf(key, data.perPage)),
              total: data.total + 1,
            });
          }
        }
      }

      const counts = queryClient.getQueryData<StageCountsDto>(
        inquiryKeys.stageCounts(countryCode),
      );
      if (counts !== undefined) {
        snapshots.push({ key: inquiryKeys.stageCounts(countryCode), data: counts });
        queryClient.setQueryData(
          inquiryKeys.stageCounts(countryCode),
          applyMoveToCounts(counts, move),
        );
      }

      const record = queryClient.getQueryData<InquiryRecordDto>(inquiryKeys.record(move.id));
      if (record !== undefined) {
        snapshots.push({ key: inquiryKeys.record(move.id), data: record });
        queryClient.setQueryData<InquiryRecordDto>(inquiryKeys.record(move.id), {
          ...record,
          stage: move.to,
          stage_entered_at: at,
          lost_reason: move.to === 'lost' ? move.reason : null,
        });
      }

      return snapshots;
    },

    onError: (error, _variables, snapshots) => {
      for (const snapshot of snapshots ?? []) {
        queryClient.setQueryData(snapshot.key, snapshot.data);
      }
      lastMove.current = null;
      toast.push({ tone: 'error', text: stageErrorText(error) });
    },

    onSuccess: (_record, { move, undoable }) => {
      lastMove.current = undoable ? move : null;
      toast.push({
        text: undoable ? moveToastText(move) : undoToastText(move),
        ...(undoable
          ? {
              action: {
                label: 'Undo',
                onAction: () => {
                  const target = lastMove.current;
                  if (target === null) return;
                  lastMove.current = null;
                  send(planUndo(target), false);
                },
              },
            }
          : {}),
      });
    },

    onSettled: (_data, _error, { move }) => {
      // The server is the authority on ordering, totals and `stage_entered_at`. Every
      // list for this country is invalidated rather than only the two columns involved:
      // the "All" tab, a search result and a board column can all hold the same row.
      void queryClient.invalidateQueries({ queryKey: ['inquiries', countryCode] });
      void queryClient.invalidateQueries({ queryKey: inquiryKeys.record(move.id) });
      void queryClient.invalidateQueries({
        queryKey: inquiryEventKeys.forInquiry(countryCode, move.id),
      });
    },
  });

  const send = useCallback(
    (plan: MovePlan, undoable: boolean): void => {
      switch (plan.kind) {
        case 'noop':
          return;
        case 'refused':
          toast.push({ tone: 'error', text: refusalText(plan.stage) });
          return;
        case 'reason-required':
          setAwaitingReason(plan.move);
          return;
        case 'patch':
          mutation.mutate({ move: plan.move, body: plan.body, undoable });
          return;
      }
    },
    [mutation, toast],
  );

  const requestMove = useCallback(
    (source: MoveSource, target: string, reason?: string): void => {
      send(planStageMove(source, target, reason), true);
    },
    [send],
  );

  const submitReason = useCallback(
    (reason: string): void => {
      const move = awaitingReason;
      if (move === null) return;
      const source: MoveSource = {
        id: move.id,
        doc_no: move.docNo,
        stage: move.from,
        lost_reason: move.lostReasonBefore,
      };
      const plan = planStageMove(source, move.to, reason);
      // Still short of a reason (blank input): keep the prompt open rather than firing a
      // request the API will refuse with 422.
      if (plan.kind === 'reason-required') return;
      setAwaitingReason(null);
      send(plan, true);
    },
    [awaitingReason, send],
  );

  return {
    requestMove,
    awaitingReason,
    submitReason,
    cancelReason: () => setAwaitingReason(null),
    isMoving: mutation.isPending,
  };
}

/**
 * A stage failure in words.
 *
 * `LOST_REASON_REQUIRED` is called out by name because it is the one refusal the operator
 * can fix on the spot, and the API raises it as a distinct code precisely so a client can
 * say so instead of showing "422".
 */
export function stageErrorText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'LOST_REASON_REQUIRED') {
      return 'Moving to Lost needs a reason. Nothing was changed.';
    }
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That inquiry belongs to another country. Nothing was changed.';
    }
    if (error.status === 403) return 'You do not have permission to move stages here.';
    if (error.status === 404) return 'That inquiry no longer exists. Nothing was changed.';
    return `${error.message} Nothing was changed.`;
  }
  return 'The stage could not be changed. Nothing was changed.';
}

/* ─────────────────────────────────────────────────────────────────── bulk ── */

export function useBulkUpdate(
  countryCode: string,
): UseMutationResult<
  BulkResultDto & { action: BulkAction; requested: number },
  unknown,
  { ids: string[]; action: BulkAction; value?: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    // The result is widened with what was ASKED FOR, because `{updated, refused}` alone
    // cannot be reported honestly: `updated` is an end-state claim, so "3 of 5" needs the
    // 5, and the wording differs between an assign and a stage move.
    mutationFn: async (input) => {
      const result = await bulkUpdateInquiries(countryCode, input);
      return { ...result, action: input.action, requested: input.ids.length };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries', countryCode] });
    },
  });
}

/* ──────────────────────────────────────────────────────────────── creates ── */

export function useCreateInquiry(
  countryCode: string,
): UseMutationResult<InquiryRecordDto, unknown, CreateInquiryBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createInquiry(countryCode, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries', countryCode] });
    },
  });
}

export function useCreateParty(
  countryCode: string,
): UseMutationResult<PartyDto, unknown, CreatePartyBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createParty(countryCode, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parties', countryCode] });
    },
  });
}

/**
 * The requirements editor's save (unit 3.2) — `PATCH /inquiries/:id` carrying ONLY
 * `platforms` (see `UpdateInquiryBody` for why the body type admits nothing else).
 *
 * The variables are the ENTRIES, not the whole body, so a caller structurally cannot
 * express "send the key with nothing decided": deciding to mutate IS deciding the set.
 * The untouched case never reaches this hook — `RequirementsGroup` disables its Save
 * until the set differs from the record, which is what keeps D18's `undefined` =
 * untouched true from the browser.
 *
 * INVALIDATED, NOT HAND-PATCHED: the record (this screen), the country prefix (the
 * MODULES cells on every cached list, the stage counts, and the picker options — one
 * small re-read each, and each can only get more current), and the record's event feed
 * (the write emits `inquiry.updated`, and the Activity tab must show it without a
 * reload). The optimistic cache surgery `useStageMoveController` does is for a drag
 * gesture whose card must not vanish mid-flight; a form save has no such constraint,
 * and a snapshot echo would be a second code path that can drift.
 */
export function useUpdateInquiryPlatforms(
  countryCode: string,
  inquiryId: string,
): UseMutationResult<InquiryRecordDto, unknown, InquiryPlatformEntry[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (platforms) => updateInquiry(inquiryId, { platforms }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inquiryKeys.record(inquiryId) });
      void queryClient.invalidateQueries({ queryKey: ['inquiries', countryCode] });
      void queryClient.invalidateQueries({
        queryKey: inquiryEventKeys.forInquiry(countryCode, inquiryId),
      });
    },
  });
}

export function useCreateFollowup(
  countryCode: string,
  inquiryId: string,
): UseMutationResult<unknown, unknown, CreateFollowupBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createFollowup(inquiryId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inquiryKeys.record(inquiryId) });
      void queryClient.invalidateQueries({
        queryKey: inquiryEventKeys.forInquiry(countryCode, inquiryId),
      });
    },
  });
}
